import { Link, useNavigate } from "react-router-dom";
import { X, Trash2, Plus, Minus, ShoppingBag, Gift } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/api";
import { usePromo } from "./PromoBanner";

export const CartDrawer = () => {
  const { items, open, setOpen, subtotalFormatted, remove, setQty, count } = useCart();
  const promo = usePromo();
  const isBogo = promo?.active && promo.type === "bogo";
  const nav = useNavigate();
  if (!open) return null;

  const close = () => setOpen(false);

  return (
    <div
      data-testid="cart-drawer"
      className="fixed inset-0 z-[80] flex justify-end"
    >
      <div
        onClick={close}
        className="absolute inset-0 bg-[#000000]/70 backdrop-blur-sm"
      />
      <aside className="relative w-full sm:max-w-md bg-[#0F0C0A] border-l border-[#2D2622] flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#2D2622]">
          <div className="flex items-center gap-3 text-[#F4EAD9]">
            <ShoppingBag size={16} strokeWidth={1.2} />
            <p className="text-[11px] tracking-overline uppercase font-bold">
              Cart · {count} item{count === 1 ? "" : "s"}
            </p>
          </div>
          <button data-testid="cart-close" onClick={close} aria-label="Close">
            <X size={18} strokeWidth={1.2} className="text-[#B0A496] hover:text-[#F4EAD9]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div data-testid="cart-empty" className="p-12 text-center">
              <p className="font-serif-display text-3xl text-[#F4EAD9]">Empty.</p>
              <p className="mt-3 text-sm text-[#B0A496]">
                Your dresser awaits its first flacon.
              </p>
              <Link
                to="/shop"
                onClick={close}
                className="mt-8 inline-block text-[11px] tracking-overline uppercase font-bold text-[#D4AF37] editorial-link"
              >
                Browse fragrances
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-[#2D2622]">
              {items.map((it) => (
                <li
                  key={it.product_id}
                  data-testid={`cart-item-${it.product_id}`}
                  className="flex gap-4 p-4"
                >
                  <div className="w-20 aspect-square bg-[#1F1A17] overflow-hidden">
                    <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-serif-display text-lg text-[#F4EAD9] leading-tight">
                        {it.name}
                      </p>
                      <button
                        data-testid={`cart-remove-${it.product_id}`}
                        onClick={() => remove(it.product_id)}
                        className="text-[#B0A496] hover:text-[#E36B6B]"
                      >
                        <Trash2 size={14} strokeWidth={1.2} />
                      </button>
                    </div>
                    <p className="text-xs tabular-nums text-[#D4AF37]">
                      {formatPrice(it.price_inr)}
                    </p>
                    {isBogo && (
                      <p
                        data-testid={`cart-bogo-${it.product_id}`}
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-overline uppercase font-bold text-[#D4AF37]"
                      >
                        <Gift size={12} strokeWidth={1.4} />
                        + {it.qty} free · Buy 1 Get 1
                      </p>
                    )}
                    <div className="mt-auto flex items-center gap-2">
                      <button
                        data-testid={`cart-dec-${it.product_id}`}
                        onClick={() => setQty(it.product_id, Math.max(1, it.qty - 1))}
                        className="border border-[#2D2622] hover:border-[#D4AF37] p-1"
                      >
                        <Minus size={12} strokeWidth={1.2} className="text-[#F4EAD9]" />
                      </button>
                      <span className="text-sm tabular-nums text-[#F4EAD9] min-w-[24px] text-center">
                        {it.qty}
                      </span>
                      <button
                        data-testid={`cart-inc-${it.product_id}`}
                        onClick={() => setQty(it.product_id, it.qty + 1)}
                        className="border border-[#2D2622] hover:border-[#D4AF37] p-1"
                      >
                        <Plus size={12} strokeWidth={1.2} className="text-[#F4EAD9]" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#2D2622] p-6 space-y-4">
            <div className="flex items-baseline justify-between">
              <p className="text-[11px] tracking-overline uppercase font-bold text-[#B0A496]">
                Subtotal
              </p>
              <p
                data-testid="cart-subtotal"
                className="text-xl tabular-nums text-[#D4AF37] font-medium"
              >
                ₹{subtotalFormatted}
              </p>
            </div>
            <p className="text-xs text-[#B0A496]">
              Free shipping PAN India · Composed in India.
            </p>
            <button
              data-testid="cart-checkout"
              onClick={() => {
                close();
                nav("/checkout");
              }}
              className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-[#15110F] px-6 py-4 text-[11px] tracking-overline uppercase font-bold transition-colors"
            >
              Proceed to checkout
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
